import { Box, Stack, Typography, useTheme } from '@mui/material';
import { formatNumber } from 'components/FormCreatorComponent/helpers';
import { WarningDialog } from 'components/WarningDialog';
import { pagesEnum } from 'constants/ui.constants';
import { format, isValid, parseISO } from 'date-fns';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { BeneficiaryDashboardTable } from 'sections/MainDashboard/BeneficiaryDashboardTable';
import {
	getBeneficiaryDashboardTotal,
	getError as getBalanceError,
	setError as setBalanceError,
} from 'state/reducers/balance.reducer';
import { getBeneficiaryDatesRange } from 'state/reducers/dashboard.reducer';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { Page } from 'theme/elements/Page';

export const BeneficiaryDashboardPage = () => {
	const [searchParams] = useSearchParams();
	const dispatch = useAppDispatch();
	const theme = useTheme();
	const t = useAppSelector(selectTranslations);
	const storedRange = useAppSelector(getBeneficiaryDatesRange);
	const total = useAppSelector(getBeneficiaryDashboardTotal);
	const balanceError = useAppSelector(getBalanceError);
	const pagePrefix = 'page.dashboard.dashboardBalances.';

	const datesRange = useMemo(() => {
		const startDate = parseISO(searchParams.get('dateFrom') || '');
		const endDate = parseISO(searchParams.get('dateTo') || '');
		return isValid(startDate) && isValid(endDate) && endDate >= startDate
			? { startDate, endDate }
			: storedRange;
	}, [searchParams, storedRange]);

	const formattedRange = `${format(datesRange.startDate, 'dd/MM/yyyy')} – ${format(
		datesRange.endDate,
		'dd/MM/yyyy',
	)}`;

	return (
		<Page title="">
			<WarningDialog
				open={balanceError.exist}
				subTitle={balanceError.text}
				handleCancel={() => {
					dispatch(setBalanceError({ exist: false }));
				}}
			/>
			<Stack gap={3}>
				<Stack>
					<Stack flexDirection="row" justifyContent="space-between">
						<Typography variant="pageTitle" display="flex" flexDirection="row" gap={2}>
							<img src="/icons/contact.svg" width={28} height={28} alt="" />
							{t(pagePrefix + 'beneficiaries')}
							<Box fontWeight={300}>({formatNumber(total)})</Box>
						</Typography>
						<Link style={{ color: theme.palette.root.formTitle }} to={'/' + pagesEnum.MAIN_DASHBOARD}>
							{t(pagePrefix + 'backToDashboard')}
						</Link>
					</Stack>
					<Typography variant="subtitle" color={theme.palette.root.grey} sx={{ mt: 0.5 }}>
						<span>{t('label.lastUpdated')} · </span>
						<span dir="ltr">{formattedRange}</span>
					</Typography>
					<Typography
						variant="captionRegular"
						color={theme.palette.root.grey}
						sx={{ mt: 1, display: 'block', maxWidth: 720 }}
					>
						{t('page.dashboard.beneficiaryTable.disclaimer.responseTime')}
					</Typography>
					<Typography
						variant="captionRegular"
						color={theme.palette.root.grey}
						sx={{ mt: 0.5, display: 'block', maxWidth: 720 }}
					>
						{t('page.dashboard.beneficiaryTable.disclaimer.excelExport')}
					</Typography>
				</Stack>
				<BeneficiaryDashboardTable datesRange={datesRange} />
			</Stack>
		</Page>
	);
};
